import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, ArrowRight, Printer, Download } from "lucide-react";
import { devWarn } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const orderNumber = searchParams.get("order");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    if (!orderNumber && !sessionId) { setStatus("error"); return; }
    let attempts = 0;
    let cancelled = false;

    const fetchOrder = async () => {
      if (!orderNumber) return null;
      try {
        const r = await axios.get(`${API}/orders/${orderNumber}`);
        return r.data;
      } catch { return null; }
    };

    const triggerStripeSync = async () => {
      // Tells backend to verify payment with Stripe and mark order paid
      if (!sessionId) return;
      try { await axios.get(`${API}/checkout/status/${sessionId}`); } catch (err) { devWarn("Stripe sync failed:", err.message); }
    };

    const poll = async () => {
      if (cancelled) return;
      const orderData = await fetchOrder();
      if (orderData && orderData.payment_status === "paid") {
        setOrder(orderData);
        setStatus("paid");
        return;
      }
      // Order not paid yet — trigger backend to sync with Stripe, then retry
      await triggerStripeSync();
      if (attempts < 10) { attempts++; setTimeout(poll, 2000); }
      else setStatus("timeout");
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, orderNumber]);

  const handlePrint = () => window.print();

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <h1 className="font-['Playfair_Display'] text-3xl text-white mb-3">Bearbetar betalning...</h1>
          <p className="text-white/50 font-['Outfit'] font-light">Vänligen vänta medan vi bekräftar din betalning.</p>
        </div>
      </div>
    );
  }

  if (status !== "paid" || !order) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-['Playfair_Display'] text-3xl text-white mb-3">{status === "expired" ? "Sessionen har löpt ut" : "Något gick fel"}</h1>
          <p className="text-white/50 font-['Outfit'] font-light mb-8">Kontakta oss om du behöver hjälp.</p>
          <Link to="/" className="btn-secondary inline-flex items-center gap-2 px-8 py-4 text-xs tracking-[0.15em] uppercase font-['Outfit']">Tillbaka till events</Link>
        </div>
      </div>
    );
  }

  const paidDate = order.paid_at ? new Date(order.paid_at) : new Date();

  return (
    <main className="min-h-screen bg-[#0A0A0A] pt-28 pb-24">
      <div className="max-w-2xl mx-auto px-6">
        {/* Success banner */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto mb-5">
            <Check size={28} className="text-emerald-500" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-2">Betalning bekräftad</h1>
          <p className="text-white/50 font-['Outfit'] font-light">Tack för ditt köp. Din upplevelse är säkrad.</p>
        </motion.div>

        {/* Receipt card */}
        <motion.div
          ref={receiptRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          data-testid="receipt-card"
          className="bg-[#141414] border border-[#262626] overflow-hidden print:bg-white print:text-black"
        >
          {/* Receipt header */}
          <div className="p-8 pb-6 border-b border-[#262626]">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[#C5A059] font-semibold text-sm tracking-[0.3em] uppercase font-['Outfit']">SERAVA</span>
                <span className="text-white/40 text-[9px] tracking-[0.25em] uppercase font-['Outfit'] font-light ml-1">SPORTSEVENTS</span>
              </div>
              <div className="text-right">
                <p className="text-xs text-white/40 font-['Outfit'] uppercase tracking-wider">Kvitto</p>
                <p className="text-[#C5A059] font-['Outfit'] font-semibold text-sm mt-1">{order.order_number}</p>
              </div>
            </div>
          </div>

          {/* Customer & date info */}
          <div className="p-8 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-6 border-b border-[#262626]">
            <div className="min-w-0">
              <p className="text-[10px] text-white/40 font-['Outfit'] uppercase tracking-wider mb-2">Fakturerad till</p>
              <p className="text-white font-['Outfit'] font-medium text-sm break-words">{order.customer_name}</p>
              {order.customer_company && <p className="text-white/60 font-['Outfit'] font-light text-sm break-words">{order.customer_company}</p>}
              {order.org_number && <p className="text-white/40 font-['Outfit'] font-light text-xs">Org. {order.org_number}</p>}
              <p className="text-white/40 font-['Outfit'] font-light text-xs mt-1 break-all">{order.customer_email}</p>
              {order.customer_phone && <p className="text-white/40 font-['Outfit'] font-light text-xs">{order.customer_phone}</p>}
              {order.billing_address && (
                <p className="text-white/40 font-['Outfit'] font-light text-xs mt-1 break-words">
                  {order.billing_address}{order.billing_zip && `, ${order.billing_zip}`}{order.billing_city && ` ${order.billing_city}`}
                </p>
              )}
            </div>
            <div className="sm:text-right min-w-0">
              <p className="text-[10px] text-white/40 font-['Outfit'] uppercase tracking-wider mb-2">Kvittoinformation</p>
              <div className="space-y-1 text-sm font-['Outfit']">
                <p className="text-white/60"><span className="text-white/40">Datum: </span>{paidDate.toLocaleDateString('sv-SE')}</p>
                <p className="text-white/60"><span className="text-white/40">Betalning: </span>Kort</p>
                <p className="text-emerald-400 font-medium">Betald</p>
              </div>
              {order.reference && <p className="text-white/40 font-['Outfit'] font-light text-xs mt-2">Ref: {order.reference}</p>}
            </div>
          </div>

          {/* Line items table */}
          <div className="p-6 sm:p-8 border-b border-[#262626] overflow-x-auto">
            <table className="w-full text-sm font-['Outfit']">
              <thead>
                <tr className="text-white/40 text-[10px] uppercase tracking-wider border-b border-[#262626]">
                  <th className="text-left pb-3 font-medium">Beskrivning</th>
                  <th className="text-right pb-3 font-medium">Antal</th>
                  <th className="text-right pb-3 font-medium hidden sm:table-cell">Pris</th>
                  <th className="text-right pb-3 font-medium">Belopp</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#262626]/50">
                  <td className="py-4 pr-2">
                    <p className="text-white font-medium break-words">{order.package_name}</p>
                    <p className="text-white/40 text-xs mt-0.5 break-words">{order.event_title}{order.event_subtitle ? ` — ${order.event_subtitle}` : ''}</p>
                    <p className="text-white/30 text-xs">{order.event_date}</p>
                  </td>
                  <td className="text-right text-white/60 py-4 align-top">{order.quantity}</td>
                  <td className="text-right text-white/60 py-4 align-top hidden sm:table-cell whitespace-nowrap">{order.package_price?.toLocaleString('sv-SE')} {order.currency}</td>
                  <td className="text-right text-white font-medium py-4 align-top whitespace-nowrap">{(order.subtotal_ex_vat ?? order.total_amount)?.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {order.currency}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="p-6 sm:p-8 pb-6">
            <div className="flex justify-end">
              <div className="w-full sm:w-72 space-y-2 text-sm font-['Outfit']">
                <div className="flex justify-between text-white/50">
                  <span>Delsumma (ex moms)</span>
                  <span className="text-white whitespace-nowrap">{(order.subtotal_ex_vat ?? order.total_amount)?.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {order.currency}</span>
                </div>
                {order.vat_amount > 0 && (
                  <div className="flex justify-between text-white/50">
                    <span>Moms ({Math.round((order.vat_rate || 0) * 100)}%)</span>
                    <span className="text-white whitespace-nowrap">{order.vat_amount?.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {order.currency}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-[#262626] pt-3 mt-3">
                  <span className="text-white font-semibold text-base">Totalt{order.vat_amount > 0 ? ' (inkl moms)' : ''}</span>
                  <span className="text-white font-semibold text-lg whitespace-nowrap">{order.total_amount?.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {order.currency}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer — company info */}
          <div className="px-6 sm:px-8 py-6 bg-[#0A0A0A] border-t border-[#262626] space-y-1">
            <p className="text-white/70 font-['Outfit'] text-xs font-semibold">Pangea Nexus AB</p>
            <p className="text-white/40 font-['Outfit'] text-[11px]">Org.nr 556822-5139</p>
            <p className="text-white/40 font-['Outfit'] text-[11px]">Nybohovsbacken 36 b, 117 63 Stockholm</p>
            <p className="text-white/40 font-['Outfit'] text-[11px] break-words">Tel: 08-587 086 00 · contact@seravagroup.com</p>
            <p className="text-white/25 font-['Outfit'] text-[11px] pt-2">Detta kvitto gäller som betalningsbekräftelse.</p>
          </div>
        </motion.div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.order_number}/receipt.pdf`}
            download data-testid="download-receipt-btn"
            className="btn-secondary px-6 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center justify-center gap-2">
            <Download size={14} /> Ladda ner PDF
          </a>
          <button onClick={handlePrint} data-testid="print-receipt-btn"
            className="btn-secondary px-6 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center justify-center gap-2">
            <Printer size={14} /> Skriv ut
          </button>
          <Link to="/" data-testid="back-to-events-btn"
            className="btn-primary px-6 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center justify-center gap-2">
            Tillbaka till events <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </main>
  );
}
